/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

import { FormApi } from 'final-form'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { SwitchField } from 'components/Switch'
import { MembershipLevelSelect } from 'components/MembershipLevelSelect'
import { validateRequired } from 'utils/validation'
import { AITestResponseTypeEnum, AITestTypeEnum, MembershipType } from '__generatedTypes__/globalTypes'
import { findOneAiTest_findOneAiTest as AITest } from '__generatedTypes__/findOneAiTest'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import * as commonClasses from '../styles'
import { validateVideoLinkRequired } from './utils'
import * as classes from './styles'

export const QUESTIONS_COUNT = 3
export const FLT_QUESTIONS_COUNT = 2

const PROMPT_TYPE_OPTIONS = [
  {
    id: AITestTypeEnum.TextTest,
    title: 'Text Prompt',
  },
  {
    id: AITestTypeEnum.VideoTest,
    title: 'Video Prompt',
  },
]

export type FormValues = {
  id?: string
  title: string
  description: string
  contextText: string
  contextVideoUrl: string
  videoText: string
  type?: { id: AITestTypeEnum; title: string }
  membershipLevel?: MembershipType
  isLive: boolean

  question1: string
  videoBestResponse1: string
  textBestResponse1: string
  isLive1: boolean

  question2: string
  videoBestResponse2: string
  textBestResponse2: string
  isLive2: boolean

  question3: string
  videoBestResponse3: string
  textBestResponse3: string
  isLive3: boolean
}

type Props = {
  responseTest?: AITest
  type?: AITestTypeEnum | null
  responseType?: AITestResponseTypeEnum | null
  onSubmit: (input: FormValues, form: FormApi<FormValues>) => Promise<void>
  isFltTest?: boolean
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

const getInitialValues = (test?: AITest): Partial<FormValues> => ({
  id: test?.id,
  title: test?.title || '',
  contextText: test?.contextText || '',
  contextVideoUrl: test?.contextVideoUrl || '',
  videoText: test?.videoText || '',
  description: test?.description || '',
  type: test?.type ? PROMPT_TYPE_OPTIONS.find(({ id }) => id === test.type) : undefined,
  membershipLevel: test?.membershipLevel,
  isLive: test?.isLive ?? false,

  question1: test?.questions[0]?.questionText || '',
  textBestResponse1: test?.questions[0]?.bestResponseText || '',
  videoBestResponse1: test?.questions[0]?.bestResponseVideoUrl || '',
  isLive1: test?.questions[0]?.isLive ?? true,

  question2: test?.questions[1]?.questionText || '',
  textBestResponse2: test?.questions[1]?.bestResponseText || '',
  videoBestResponse2: test?.questions[1]?.bestResponseVideoUrl || '',
  isLive2: test?.questions[1]?.isLive ?? true,

  question3: test?.questions[2]?.questionText || '',
  textBestResponse3: test?.questions[2]?.bestResponseText || '',
  videoBestResponse3: test?.questions[2]?.bestResponseVideoUrl || '',
  isLive3: test?.questions[2]?.isLive ?? true,
})

const getEntityName = (isFlt?: boolean, type?: AITestTypeEnum | null, responseType?: AITestResponseTypeEnum | null) => {
  if (isFlt) {
    switch (responseType) {
      case AITestResponseTypeEnum.TextResponse:
        return 'Typed-Response Test'
      case AITestResponseTypeEnum.VideoResponse:
        return 'Video-Response Test'
      default:
        return 'AI Test'
    }
  }

  switch (type) {
    case AITestTypeEnum.TextTest:
      return 'Text Prompt Test'
    case AITestTypeEnum.VideoTest:
      return 'Video Prompt Test'
    default:
      return 'AI Test'
  }
}

export const Form = ({ responseTest, onSubmit, type: typeProp, responseType: responseTypeProp, isFltTest }: Props) => {
  const type = (responseTest?.type || typeProp) as AITestTypeEnum | null
  const responseType = (responseTest?.responseType || responseTypeProp) as AITestResponseTypeEnum | null
  const isEditing = !!responseTest?.id

  const [submitting, setSubmitting] = useState(false)
  const [initialValues, setInitialValues] = useState(getInitialValues(responseTest))

  useEffect(() => {
    setInitialValues(getInitialValues(responseTest))
  }, [responseTest?.id])

  const handleSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    try {
      setSubmitting(true)
      await onSubmit(values, form)
    } catch (e) {
      console.error(e)
    }
    setSubmitting(false)
  }

  return (
    <div css={commonClasses.container} data-testid="response-test-form-wrapper">
      <Typography variant="title3" css={classes.title} data-testid="response-test-title">
        {isEditing ? 'Edit' : 'Create'} {getEntityName(isFltTest, type, responseType)}
        {isFltTest ? ' for FLT' : ''}
      </Typography>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ values, handleSubmit, dirty }) => {
          const selectedType = values.type?.id ?? type
          const isTextPromptTest = selectedType === AITestTypeEnum.TextTest

          return (
            <form onSubmit={handleSubmit} css={classes.block} data-testid="response-test-form">
              <InputField
                name="title"
                validate={validateRequired}
                label="Title"
                placeholder="Enter Title"
                fullWidth
                data-testid="title-input"
              />
              {isFltTest ? (
                <SelectField
                  name="type"
                  label="Type of Prompt"
                  items={PROMPT_TYPE_OPTIONS}
                  placeholder="Choose Type of Prompt"
                  disabled={isEditing}
                  fullWidth
                  data-testid="type-select"
                />
              ) : (
                <>
                  <InputField
                    name="description"
                    label="Description"
                    placeholder="Enter Description"
                    fullWidth
                    data-testid="description-input"
                  />
                  <MembershipLevelSelect name="membershipLevel" validate={validateRequired} fullWidth />
                </>
              )}

              {!!selectedType && (
                <>
                  {isTextPromptTest ? (
                    <InputField
                      name="contextText"
                      validate={validateRequired}
                      label="Text Prompt"
                      placeholder="Enter Text Prompt"
                      fullWidth
                      data-testid="text-prompt-input"
                    />
                  ) : (
                    <>
                      <TextareaAutosize
                        name="videoText"
                        validate={validateRequired}
                        label="Video Text"
                        placeholder="Enter Video Text"
                        css={classes.textArea}
                        data-testid="video-text-input"
                      />

                      <InputField
                        name="contextVideoUrl"
                        validate={validateVideoLinkRequired}
                        label="Video Prompt"
                        placeholder="Enter Video Prompt"
                        fullWidth
                        data-testid="video-prompt-input"
                      />
                    </>
                  )}

                  {Array.from({ length: isFltTest ? FLT_QUESTIONS_COUNT : QUESTIONS_COUNT }).map((_, index) => {
                    const order = index + 1

                    return (
                      <React.Fragment key={index}>
                        <div css={classes.divider} />

                        <InputField
                          name={`question${order}`}
                          validate={validateRequired}
                          label={`Question #${order}`}
                          placeholder={`Enter Question #${order}`}
                          fullWidth
                          data-testid={`question${order}-input`}
                          disabled={isEditing}
                        />
                        {!isFltTest && <SwitchField name={`isLive${order}`} label="Live" />}
                        {(!isFltTest || responseType === AITestResponseTypeEnum.VideoResponse) && (
                          <InputField
                            name={`videoBestResponse${order}`}
                            label="Best Response Video URL"
                            placeholder="Enter Best Response Video URL"
                            fullWidth
                            data-testid={`videoBestResponse${order}-input`}
                          />
                        )}
                        {(!isFltTest || responseType === AITestResponseTypeEnum.TextResponse) && (
                          <TextareaAutosize
                            name={`textBestResponse${order}`}
                            label="Best Response Text"
                            placeholder="Enter Best Response Text"
                            css={classes.textArea}
                            data-testid={`textBestResponse${order}-input`}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                </>
              )}

              <Button color="blue" type="submit" size="large" disabled={!dirty || submitting || !selectedType}>
                {isEditing ? 'Save Edits' : 'Create'}
              </Button>
            </form>
          )
        }}
      />
    </div>
  )
}
